import React from 'react'
import { GiTakeMyMoney, GiTestTubes } from 'react-icons/gi'
import {
	FaUsers,
	FaTruck,
	FaGlobeEurope,
	FaChartBar,
	FaPowerOff,
	FaMapMarkedAlt,
	FaUserCircle,
	FaDollarSign,
} from 'react-icons/fa'
import { SubMenuItemComponent } from '../LayoutClient/menu-item.component'

// Consistent style for icons
const iconStyle = { color: 'white', fontSize: '18px' }

/**
 * @typedef {Object} SubMenuItem
 * @property {string} id - Unique identifier for the sub menu item
 * @property {string} title - Display text for the sub menu item
 * @property {string} path - Navigation path
 * @property {Function} [hideWhen] - Function that takes a user and returns boolean indicating if item should be hidden
 */

/**
 * @typedef {Object} MenuItem
 * @property {string} id - Unique identifier for the menu item
 * @property {string} title - Display text for the menu item
 * @property {string} [path] - Navigation path (optional if subMenu or action is provided)
 * @property {React.ReactNode} icon - Icon component
 * @property {string} [action] - Special action identifier (e.g., 'logout')
 * @property {Function} [hideWhen] - Function that takes a user and returns boolean indicating if item should be hidden
 * @property {SubMenuItem[]} [subMenu] - Array of submenu items
 */

/**
 * Helper function to safely check user role
 * @param {Object|null|undefined} user - User object which might be null/undefined
 * @param {string|string[]} roles - Role or array of roles to check
 * @returns {boolean} - Whether the user has any of the specified roles
 */
const hasRole = (user, roles) => {
	// Check if user and user.role exist
	if (!user || !user.role) return false

	// If roles is an array, check if user.role is in the array
	if (Array.isArray(roles)) {
		return roles.includes(user.role)
	}

	// If roles is a string, directly compare
	return user.role === roles
}

/** @type {MenuItem[]} */
export const adminMenuItems = [
	{
		id: 'clients',
		title: 'Clients',
		icon: <FaUsers style={iconStyle} />,
		hideWhen: (user) =>
			!hasRole(user, [
				'admin',
				'super admin',
				'admin distribution',
				'admin service après vente',
			]),
		subMenu: [
			{
				id: 'mes-clients',
				title: 'Mes clients',
				path: '/admin/clients',
			},
			{
				id: 'archived-clients',
				title: 'Archives',
				path: '/admin/archive/clients',
				hideWhen: (user) => hasRole(user, 'admin service après vente'),
			},
		],
	},
	{
		id: 'transporteurs',
		title: 'Transporteurs',
		icon: <FaTruck style={iconStyle} />,
		hideWhen: (user) =>
			!hasRole(user, [
				'admin',
				'super admin',
				'admin distribution',
				'admin service après vente',
			]),
		subMenu: [
			{
				id: 'transporteurs-old',
				title: 'Mes transporteurs',
				path: '/admin/transporteurs',
			},
			{
				id: 'transporteurs-new',
				title: 'Nouveau',
				path: '/admin/transporteurs/new',
				hideWhen: (user) => hasRole(user, 'admin service après vente'),
			},
			{
				id: 'transporteurs-delete',
				title: 'Archives',
				path: '/admin/transporteurs/delete',
				hideWhen: (user) => hasRole(user, 'admin service après vente'),
			},
		],
	},
	{
		id: 'chauffeurs',
		title: 'Chauffeurs',
		icon: <FaTruck style={iconStyle} />,
		hideWhen: (user) =>
			!hasRole(user, [
				'admin',
				'super admin',
				'admin distribution',
				'admin service après vente',
			]),
		subMenu: [
			{
				id: 'chauffeurs-1',
				title: 'Mes chauffeurs',
				path: '/admin/chauffeurs',
			},
		],
	},
	{
		id: 'administrateurs',
		title: 'Administrateurs',
		icon: <FaUsers style={iconStyle} />,
		hideWhen: (user) => !hasRole(user, 'super admin'),
		subMenu: [
			{
				id: 'administrateurs-list',
				title: 'Mes Administrateurs',
				path: '/admin/administrateurs',
			},
			{
				id: 'administrateurs-new',
				title: 'Nouveau',
				path: '/admin/administrateurs/new',
			},
			{
				id: 'administrateurs-archived',
				title: 'Archives',
				path: '/admin/archived',
				hideWhen: (user) => hasRole(user, 'admin service après vente'),
			},
		],
	},
	{
		id: 'commerciaux',
		title: 'Commerciaux',
		icon: <FaUsers style={iconStyle} />,
		hideWhen: (user) =>
			!hasRole(user, [
				'admin',
				'super admin',
				'admin distribution',
				'admin service après vente',
			]),
		subMenu: [
			{
				id: 'commerciaux-list',
				title: 'Mes Commerciaux',
				path: '/admin/commerciaux',
			},
			{
				id: 'commerciaux-new',
				title: 'Nouveau',
				path: '/admin/commercant/new',
				hideWhen: (user) => !hasRole(user, ['admin', 'super admin']),
			},
			{
				id: 'commerciaux-archived',
				title: 'Archives',
				path: '/admin/commerciaux/archived',
				hideWhen: (user) => hasRole(user, 'admin service après vente'),
			},
		],
	},
	{
		id: 'plate-forme',
		title: 'Plate-forme',
		icon: <FaGlobeEurope style={iconStyle} />,
		subMenu: [
			{
				id: 'plateforme-commandes',
				title: 'Commandes',
				path: '/admin/plateforme/commandes',
				hideWhen: (user) =>
					!hasRole(user, [
						'admin',
						'super admin',
						'admin distribution',
						'admin service après vente',
					]),
			},
			{
				id: 'plateforme-attribuer',
				title: 'Attribuer',
				path: '/admin/plateforme/attribuer',
				hideWhen: (user) =>
					!hasRole(user, [
						'admin',
						'super admin',
						'admin distribution',
						'admin service après vente',
					]),
			},
			{
				id: 'plateforme-salon',
				title: 'Salon',
				path: '/admin/plateforme/salon',
				hideWhen: (user) =>
					!hasRole(user, [
						'admin',
						'super admin',
						'admin distribution',
						'admin service après vente',
					]),
			},
			{
				id: 'plateforme-annulees',
				title: 'Annulées',
				path: '/annulees/admin',
				hideWhen: (user) =>
					!hasRole(user, [
						'admin',
						'super admin',
						'admin distribution',
					]),
			},
			{
				id: 'plateforme-terminees',
				title: 'Terminées',
				path: '/terminer/admin',
				hideWhen: (user) =>
					!hasRole(user, [
						'admin',
						'super admin',
						'admin distribution',
					]),
			},
			{
				id: 'plateforme-facturation',
				title: 'Facturation',
				path: '/admin/plateforme/facturation',
				hideWhen: (user) =>
					!hasRole(user, [
						'admin',
						'super admin',
						'admin comptabilité',
					]),
			},
			{
				id: 'plateforme-anomalie',
				title: 'Anomalie Transport',
				path: '/admin/plateforme/anomalie-transport',
			},
			{
             	id: 'plateforme-supprimees',
	            title: 'Supprimées',
	            path: '/admin/plateforme/commandes-supprimees',
	
            },
		],
	},
	{
		id: 'comptabilite',
		title: 'Comptabilité',
		icon: <FaChartBar style={iconStyle} />,
		hideWhen: (user) =>
			!hasRole(user, ['admin', 'super admin', 'admin comptabilité']),
		subMenu: [
			{
				id: 'comptabilite-clients',
				title: 'Clients',
				path: '/admin/comptabilite/clients',
			},
			{
				id: 'comptabilite-transporteurs',
				title: 'Transporteurs',
				path: '/admin/comptabilite/transporteurs',
			},
			{
				id: 'comptabilite-commerciaux',
				title: 'Commerciaux',
				path: '/admin/comptabilite/commerciaux',
				hideWhen: (user) => !hasRole(user, 'admin comptabilité'),
			},
		],
	},
	{
		id: 'prelevement',
		title: 'Prélèvement',
		icon: <GiTakeMyMoney style={{ color: 'white', fontSize: '22px' }} />,
		hideWhen: (user) =>
			!hasRole(user, ['admin', 'super admin', 'admin comptabilité']),
		subMenu: [
			{
				id: 'prelevement-list',
				title: 'Prélèvements',
				path: '/admin/prelevement',
			},
			{
				id: 'prelevement-failed',
				title: 'Prélèvements Échoués',
				path: '/admin/failedcharge',
			},
		],
	},
	{
		id: 'tarif',
		title: 'Tarif',
		path: '/tarif',
		icon: <FaDollarSign style={iconStyle} />,
		hideWhen: (user) =>
			!hasRole(user, ['admin', 'super admin', 'admin comptabilité']),
	},
	{
		id: 'map',
		title: 'Map',
		path: '/map',
		icon: <FaMapMarkedAlt style={iconStyle} />,
	},
	{
		id: 'mon-compte',
		title: 'Mon Compte',
		path: '/admin/account',
		icon: <FaUserCircle style={iconStyle} />,
	},
	{
		id: 'admin-test-panel',
		title: 'adminTestPanel',
		path: '/admin/test-panel',
		icon: <GiTestTubes style={iconStyle} />,
		hideWhen: (user) =>
			!hasRole(user, ['admin', 'super admin', 'admin comptabilité']),
	},

	{
		id: 'logout',
		title: 'Déconnexion',
		icon: <FaPowerOff style={iconStyle} />,
		action: 'logout',
	},
]

/**
 * Helper function to render a submenu
 * @param {SubMenuItem[]} items - Submenu items array
 * @param {string} activeSubItem - Currently active submenu ID
 * @param {Object} user - Current user object
 * @param {Function} onClick - Click handler function
 * @returns {React.ReactNode}
 */
export const renderSubMenu = (items, activeSubItem, user, onClick) => {
	return items
		.filter((item) => !item.hideWhen || !item.hideWhen(user))
		.map((item) => (
			<SubMenuItemComponent
				key={item.id}
				item={item}
				isActive={activeSubItem === item.id}
				onClick={onClick}
			/>
		))
}
